/**
 * /about — About Elreign.
 *
 * Newsreader serif hero, brand story, team principle, South African focus.
 */

import { createFileRoute, Link } from '@tanstack/react-router';
import { MarketingLayout } from '../components/layouts/marketing-layout';

export const Route = createFileRoute('/about')({
  component: AboutPage,
});

function AboutPage() {
  return (
    <MarketingLayout>
      {/* Beno feedback (2026-07-15): a clearly visible way back to the
          marketing home, in addition to the header wordmark link. */}
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px 48px 0' }}>
        <Link
          to="/"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 14,
            fontWeight: 500,
            color: 'var(--primary-600)',
            textDecoration: 'none',
          }}
        >
          <span aria-hidden="true">←</span> Home
        </Link>
      </div>

      {/* Hero */}
      <section
        style={{
          maxWidth: 900,
          margin: '0 auto',
          padding: '72px 48px 0',
          textAlign: 'center',
        }}
      >
        <p className="eyebrow" style={{ marginBottom: 20 }}>
          About Elreign
        </p>
        <h1
          className="serif"
          style={{
            fontSize: 'clamp(44px, 7vw, 88px)',
            fontStyle: 'italic',
            fontWeight: 300,
            color: 'var(--ink)',
            lineHeight: 1.05,
            letterSpacing: '-0.025em',
            margin: '0 0 32px',
          }}
        >
          The trail has to hold.
        </h1>
        <p
          style={{
            fontSize: 20,
            color: 'var(--ink-3)',
            maxWidth: 600,
            margin: '0 auto',
            lineHeight: 1.65,
          }}
        >
          Trust administration is one of the few disciplines where a gap in the record is not an
          inconvenience. It is a breach of fiduciary duty. We built Elreign because the trail kept
          breaking.
        </p>
      </section>

      {/* Hairline rule */}
      <div
        style={{
          maxWidth: 900,
          margin: '56px auto 0',
          borderTop: '1px solid var(--hairline)',
        }}
      />

      {/* Story */}
      <section
        style={{
          maxWidth: 720,
          margin: '0 auto',
          padding: '56px 24px 0',
        }}
      >
        <div
          style={{
            fontSize: 19,
            lineHeight: 1.7,
            color: 'var(--ink-2)',
            display: 'flex',
            flexDirection: 'column',
            gap: 24,
          }}
        >
          <p>
            South African inter-generational trusts are complicated instruments. They involve
            multiple trustees with different schedules, beneficiaries at different life stages,
            FICA obligations that repeat on a rolling five-year cycle, and resolution processes
            that still, in 2026, routinely involve email attachments, courier envelopes, and
            handwritten minutes. The record is scattered across inboxes, filing cabinets, and
            the memory of a single specialist.
          </p>
          <p>
            When the specialist retires, or the beneficiary queries a distribution from 2017,
            or SARS requests documentation for an estate valuation, the consequences of a
            fragmented record are immediate and expensive. We saw this problem described the same
            way by fiduciary practitioners across the country. The tools they had were either too
            generic (generic document management, generic e-signature platforms) or too narrow
            (a single module bolted onto an existing accounting system).
          </p>
          <p>
            Elreign is purpose-built for the full lifecycle of a South African trust. Every
            resolution carries a cryptographic fingerprint. Every distribution is linked to the
            authorising resolution. Every trustee action is attributed to an authenticated
            identity. The trail is the product.
          </p>

          <div
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--hairline)',
              borderRadius: 'var(--r-xl)',
              padding: '28px 32px',
              borderLeft: '3px solid var(--primary-600)',
            }}
          >
            <p
              className="serif"
              style={{
                fontStyle: 'italic',
                fontSize: 24,
                color: 'var(--ink)',
                margin: 0,
                lineHeight: 1.35,
                letterSpacing: '-0.01em',
              }}
            >
              Calm. Direct. Specific. We designed for the administrator who has seen what breaks,
              not for the demo that impresses.
            </p>
          </div>

          <p>
            We hold ourselves to three standards. The interface must be calm: no alerts competing
            for attention, no friction where none is needed. The information must be specific:
            exact amounts, exact dates, exact references. And the record must be permanent: once
            a resolution is signed, it cannot be altered, only superseded.
          </p>
        </div>

        {/* Built in Cape Town */}
        <div
          style={{
            marginTop: 56,
            paddingTop: 40,
            borderTop: '1px solid var(--hairline)',
            display: 'flex',
            alignItems: 'center',
            gap: 32,
            flexWrap: 'wrap',
          }}
        >
          <div>
            <div className="eyebrow" style={{ marginBottom: 10 }}>
              Built in Cape Town
            </div>
            <p style={{ fontSize: 16, color: 'var(--ink-2)', lineHeight: 1.6, margin: 0, maxWidth: 480 }}>
              Elreign is a Cape Town company. Our focus is on South African law, South African
              regulators, and the families served by South African fiduciary practitioners. We are
              not trying to build a global product. We are trying to build the right product for
              this jurisdiction.
            </p>
          </div>
        </div>

        {/* CTA row */}
        <div
          style={{
            marginTop: 56,
            paddingTop: 40,
            borderTop: '1px solid var(--hairline)',
            display: 'flex',
            gap: 16,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <Link
            to="/contact"
            style={{
              background: 'var(--accent-400)',
              color: 'var(--primary-900)',
              padding: '12px 24px',
              borderRadius: 10,
              fontSize: 15,
              fontWeight: 600,
              textDecoration: 'none',
            }}
          >
            Get in touch
          </Link>
          <Link
            to="/legal/trust-centre"
            style={{
              fontSize: 15,
              color: 'var(--primary-600)',
              textDecoration: 'none',
              borderBottom: '1px solid rgba(31,77,69,0.25)',
              paddingBottom: 1,
            }}
          >
            Trust Centre
          </Link>
        </div>
      </section>

      <div style={{ height: 96 }} />
    </MarketingLayout>
  );
}
